import { helpers, zod } from '@acme/core'
import { z } from 'zod'

import ApiService from '../api.service'

function hhmm(nullish: boolean = false) {
    const obj = z.string().refine(
        value => {
            if (!value && nullish) return true
            const [hours, minutes] = value.split(':')
            return Number(hours) < 24 && Number(minutes) < 60
        },
        { message: 'O campo deve seguir o formato HH:mm' },
    )

    if (nullish) return obj.nullish()

    return obj
}

export const createEmpresaSchema = zod.EmpresaSchema.partial({
    id: true,
    clienteId: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
    layoutFolha: true,
    tipoDescontoDSR: true,
    atrasoDescontoDSR: true,
}).extend({
    documento: z.coerce
        .string()
        .trim()
        .transform(value => value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
        .refine(v => helpers.isDocumentoEmpresaValido(v), 'Documento inválido'),
    tipoDocumento: z.string(),
    cpfResponsavel: z.coerce
        .string()
        .trim()
        .transform(value => value.replace(/\D/g, ''))
        .refine(v => helpers.isCPF(v), 'CPF inválido'),
    uf: z.coerce
        .string()
        .refine(value => value.length === 2, { message: 'Obrigatório' }),
    telefone: z.coerce.string().trim().optional(),
    celular: z.coerce.string().trim().optional(),
    Cidade: z.object({
        ibge: z.coerce
            .number({ message: 'Obrigatório' })
            .int({ message: 'Obrigatório' })
            .positive({ message: 'Obrigatório' }),
    }),
    Segmento: z.object({
        nome: z.string().trim().min(1, 'Obrigatório'),
    }),
    diasRescisao: z.coerce.number().int().min(1),
    layoutFolha: z.string().optional(),
    tipoDescontoDSR: z.string().optional(),
    atrasoDescontoDSR: hhmm(true),
    mensagemAniversario: z.string().trim().max(250).optional(),
    justificativaPadraoRegistro: z
        .string()
        .trim()
        .max(190)
        .optional()
        .nullish()
        .transform(v => v || null),
    diaInicioPonto: z.coerce.number().int().min(1).max(31),
    inicioSemana: z.coerce.number().int().min(0).max(6).default(0),
    esconderLinhasOndeNaoTrabalha: z.union([
        z.enum(['S', 'N']),
        z.boolean().transform(v => (v ? 'S' : 'N')),
    ]),
})

export type CreateEmpresaInput = z.infer<typeof createEmpresaSchema>

export const updateEmpresaSchema = createEmpresaSchema
    .required({
        id: true,
        clienteId: true,
        cpfResponsavel: true,
    })
    .omit({
        createdAt: true,
        updatedAt: true,
    })

/**
 * `calculoAutomatico` só existe na tela: ele liga e desliga o horário do
 * cálculo. Sai do payload no `transform` — a API descarta campos fora do DTO,
 * mas mandar um campo inexistente só confunde quem lê a requisição.
 */
const campoCalculoAutomatico = { calculoAutomatico: z.boolean().optional() }

type CalculoAutomatico = {
    calculoAutomatico?: boolean
    hrCalculoAutomatico?: string | null
}

function exigirHoraDoCalculoAutomatico(
    { calculoAutomatico, hrCalculoAutomatico }: CalculoAutomatico,
    ctx: z.RefinementCtx,
) {
    if (!calculoAutomatico || hrCalculoAutomatico) return

    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hrCalculoAutomatico'],
        message: 'Selecione uma hora',
    })
}

function semCamposDeTela<T extends CalculoAutomatico>({
    calculoAutomatico: _,
    ...empresa
}: T) {
    return empresa
}

/** Schemas usados como resolver dos formulários — com os campos só de tela. */
export const createEmpresaFormSchema = createEmpresaSchema
    .extend(campoCalculoAutomatico)
    .superRefine(exigirHoraDoCalculoAutomatico)
    .transform(semCamposDeTela)

export const updateEmpresaFormSchema = updateEmpresaSchema
    .extend(campoCalculoAutomatico)
    .superRefine(exigirHoraDoCalculoAutomatico)
    .transform(semCamposDeTela)

export type Empresa = z.infer<typeof updateEmpresaSchema> & {
    TrocaInicioPonto: z.infer<typeof zod.TrocaInicioPontoSchema>[]
} & {
    Cidade?: z.infer<typeof zod.CidadeSchema>
} & {
    Segmento?: z.infer<typeof zod.SegmentoSchema>
} & {
    /** Presente apenas quando a listagem é feita por um Supervisor: true = acesso indireto (via centro de custo/local) */
    hidden?: boolean
}

const empresasService = new ApiService<CreateEmpresaInput, Empresa>('empresas')

export const empresas = Object.assign(empresasService, {
    aplicarSegmentoEmLote: (itens: Array<{ id: number; nome: string }>) =>
        empresasService.post<
            { itens: Array<{ id: number; nome: string }> },
            void
        >('segmento-em-lote', { itens }),
})
